import { useContext, useEffect, useMemo } from "react";
import NoElementsIcon from "@react-spectrum/s2/illustrations/linear/NoElements";

import { SELECTION_TYPE } from "@app/hooks/videoWizard";
import { useCampaignEditing } from "@app/hooks/useCampaignEditing";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import {
  hasPendingStructureAssets,
  hasPendingStructureVariations,
} from "../wizardSteps";
import { CampaignVisualsPreview } from "../CampaignVisualsPreview";
import { EditVariationDialogContainer } from "../EditVariationDialogContainer";
import { Step } from "./Step";
import { CampaignPromptFooter } from "./CampaignPromptFooter";
import { DevToolbarItem } from "../DevToolbar";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

/**
 * The campaign, as configured by the step's `campaignStructure`, with the image
 * variations (`variationKey`) and template assets (`assetName`) its fields link
 * to rendered next to them.
 *
 * Combines `ScriptStep` — the campaign preview and its `EditPrompt`
 * fine-tuning — with `ImagesVariationsStep`: the linked variations are
 * generated by the wizard's image variations pipeline (scoped to them while
 * this step is displayed) as soon as they have no image yet.
 */
export const CampaignVisualsStep = ({
  name,
  description,
  onContinue,
  onBack,
  continueLabel,
  contentLabel = "campaign",
  canEditCampaign = true,
  showPromptFooter = true,
  campaignStructure,
  promptHintsKey,
  editScope,
}) => {
  const {
    isStepPending,
    video,
    updateVideo,
    customerBranding,
    selectedCampaign,
    selectedPersona,
    template,
    customerName,
    setEditVariation,
    handleUploadAssets,
    handleAssetMenuAction,
    handleColorChange,
    scrapedColors,
    stepActionCtx,
  } = useContext(VideoWizardContext);

  const imageVariations = stepActionCtx?.imageVariations;

  // An edit targets the step's story section; the images of the step it
  // changed the source of are then generated anew.
  const editing = useCampaignEditing({
    editScope,
    onEdited: (previousCampaign, nextCampaign) => {
      imageVariations?.regenerateFromCampaignChange?.(
        previousCampaign,
        nextCampaign,
      );
    },
  });

  // Landing on the step directly — a reload, or a resumed project — never goes
  // through the step action, so the pipeline is armed here too; arming twice
  // is a no-op.
  const { run, variations: stageVariations } = imageVariations ?? {};

  useEffect(() => {
    run?.();
  }, [run]);

  // Only the linked variations the template actually defines (and enables for
  // this campaign) are waited for.
  const isVisualsPending = useMemo(
    () =>
      hasPendingStructureVariations(
        campaignStructure,
        imageVariations?.variations,
        video.data?.assets,
      ),
    [campaignStructure, imageVariations?.variations, video.data?.assets],
  );

  // As are the required template assets it shows.
  const isAssetsPending = useMemo(
    () =>
      hasPendingStructureAssets(
        campaignStructure,
        template.data?.assets,
        video.data?.assets,
      ),
    [campaignStructure, template.data?.assets, video.data?.assets],
  );

  if (
    isStepPending ||
    (template.data?.campaignSelectionType === SELECTION_TYPE.NONE &&
      !selectedCampaign)
  ) {
    return (
      <Step.Loading
        title={`Preparing your ${contentLabel}`}
        subtitle="This may take a few moments, so please keep this tab open."
        ariaLabel="Generating campaign"
        icon={<NoElementsIcon />}
      />
    );
  }

  if (customerBranding.isError) {
    return (
      <Step.Error
        title="Failed to load campaign details"
        onRetry={() => customerBranding.refetch()}
      >
        There was an error generating your campaign. Please try again.
      </Step.Error>
    );
  }

  return (
    <>
      <Step
        styles={style({
          gridArea: "content",
          display: "flex",
          flexDirection: "column",
          gap: 32,
        })}
      >
        <Step.Header
          title={name ?? `Review your ${contentLabel}`}
          subtitle={
            description ??
            "You can adjust the details and regenerate the visuals before moving to the next step."
          }
          styles={style({
            gap: 8,
            alignItems: "start",
            width: "full",
            maxWidth: 1200,
            marginBottom: 64,
            textAlign: "start",
          })}
        />

        <CampaignVisualsPreview
          campaignStructure={campaignStructure}
          selectedCampaign={selectedCampaign}
          selectedPersona={selectedPersona}
          canEditCampaign={canEditCampaign}
          video={video}
          templateData={template.data}
          imageVariations={imageVariations}
          onEditVariation={setEditVariation}
          onUploadVariation={handleUploadAssets}
          colors={scrapedColors}
          onUploadAsset={handleUploadAssets}
          onDeleteAsset={(asset) => handleAssetMenuAction("delete", asset)}
          onColorChange={handleColorChange}
        />
      </Step>

      <EditVariationDialogContainer />

      {showPromptFooter &&
        (!!selectedCampaign || !!video.data?.campaigns?.length) && (
          <CampaignPromptFooter
            editing={editing}
            placeholder={`Adjust ${contentLabel} details`}
            promptHintsKey={promptHintsKey}
          />
        )}

      <Step.Actions
        onBack={onBack}
        onContinue={onContinue}
        continueLabel={continueLabel}
        isContinueDisabled={
          (template.data?.campaignSelectionType === SELECTION_TYPE.SINGLE
            ? !customerName || !video.data?.selectedCampaignIds?.length
            : !video.data?.campaigns?.length ||
              (customerBranding.isEnabled && customerBranding.isPending)) ||
          isVisualsPending ||
          isAssetsPending
        }
      />

      <DevToolbarItem
        isPending={template.isPending}
        onPress={() => template.refetch()}
      >
        Refetch template
      </DevToolbarItem>
      {stageVariations.length > 0 && (
        <DevToolbarItem
          isDisabled={
            updateVideo.isPending || !!imageVariations?.generatingKeys?.size
          }
          onPress={() => run?.({ force: true })}
        >
          Regenerate all variations
        </DevToolbarItem>
      )}
    </>
  );
};
